import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useProject } from "../../context/ProjectContext";
import { buildWorkspaceDashboardPath, getProjectIdFromPath, getWorkspaceIdFromPath } from "../../utils/workspaceRouteUtils";
import Editor from "@monaco-editor/react";
import "../../styles/test-cases-overview.css";
import CreateTestCaseModal from "./CreateTestCaseModal";
import EditTestCaseModal from "./EditTestCaseModal";
import ViewTestCaseModal from "./ViewTestCaseModal";
import ExportOptionsModal from "../common/ExportOptionsModal";
import ImportTestCasesModal from "./ImportTestCasesModal";
import CICDIntegrationModal from "../common/CICDIntegrationModal";
import { ProjectStatusBadge, ProjectTypeBadge } from "../common";
import CreateTestCaseOptionsModal from "./CreateTestCaseOptionsModal";
import CompileScriptsOptionsModal, {
  SCRIPT_GENERATION_STARTED_MESSAGE,
} from "./CompileScriptsOptionsModal";
import ExtensionRecordingModal from "./ExtensionRecordingModal";
import HealingModal from "./HealingModal";
import SelfHealBadge from "./SelfHealBadge";
import RunTestCaseModal from "./RunTestCaseModal";
import RunDiscoveryTestCaseModal from "../wizard/steps/discovery/RunDiscoveryTestCaseModal";
import EditDiscoveryStepsModal from "./EditDiscoveryStepsModal";
import DeleteConfirmationModal from "../test-plans/DeleteConfirmationModal";
import { toast } from "react-toast";
import {
  fetchLatestDiscoveryRunId,
  getStoredDiscoveryRunId,
} from "../../utils/discoveryRunStorage";
import { refreshNotifications } from "../../utils/notificationEvents";
// import axios from "axios";
import { Pagination } from '../ui';
import axiosInstance from "../../api/axiosInstance";

// Mirrors backend TestCaseType: MANUAL = 1, RECORDED = 2, AI_GENERATED = 3
const TEST_CASE_TYPE_OPTIONS = [
  { value: 1, label: "Manual" },
  { value: 2, label: "Recorded" },
  { value: 3, label: "AI Generated" },
];

// Manual and AI generated test cases are compiled, edited and run through the
// UI Discovery flow; recorded test cases keep the extension recording flow.
const DISCOVERY_FLOW_TEST_CASE_TYPES = [1, 3];
const usesDiscoveryFlow = (testCase) =>
  DISCOVERY_FLOW_TEST_CASE_TYPES.includes(Number(testCase?.test_case_type));

const FILTER_SELECT_MENU_STYLE = {
  margin: 0,
  padding: "0.25rem 0",
  listStyle: "none",
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: "4px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
  overflowY: "auto",
  maxHeight: "240px",
  zIndex: 12000,
};

const FilterSelect = ({
  value,
  onChange,
  options,
  disabled = false,
  className = "filter-select",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuMaxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuMaxHeight && rect.top > spaceBelow;

    setMenuPosition({
      position: "fixed",
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      transform: openAbove ? "translateY(-100%)" : "none",
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value)
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={className}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.35rem",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedOption?.label ?? ""}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0, color: "#6b7280" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen &&
        menuPosition &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={{ ...FILTER_SELECT_MENU_STYLE, ...menuPosition }}
          >
            {options.map((option) => {
              const isSelected = String(value) === String(option.value);
              return (
                <li
                  key={option.value === "" ? "__empty__" : String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  style={{
                    padding: "0.4rem 0.6rem",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#fff7ed" : "transparent",
                    color: isSelected ? "#c2410c" : "#111827",
                    fontWeight: isSelected ? 500 : 400,
                  }}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
};

const TestCasesOverview = ({ onNavigateToTestSuite }) => {
  const navigate = useNavigate();
  const location = useLocation()
  const { selectedProject: contextProject } = useProject();
  const [testCases, setTestCases] = useState([]);
  const [filteredTestCases, setFilteredTestCases] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [testCasesLoading, setTestCasesLoading] = useState(false);
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [summaryCards, setSummaryCard] = useState(null);
  const [error, setError] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [searchInputDisabled, setSearchInputDisabled] = useState(false);
  const [testCaseSearchTerm, setTestCaseSearchTerm] = useState("");
  const [projectStatusFilter, setProjectStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [actionFeedback, setActionFeedback] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");
  const [expandedProjects, setExpandedProjects] = useState({});
  const [isCreateTestCaseModalOpen, setIsCreateTestCaseModalOpen] =
    useState(false);
  const [
    isCreateTestCaseOptionsModalOpen,
    setIsCreateTestCaseOptionsModalOpen,
  ] = useState(false);
  const [isExtensionRecordingModalOpen, setIsExtensionRecordingModalOpen] =
    useState(false);
  const [extensionRecordingModalMode, setExtensionRecordingModalMode] =
    useState("create");
  const [editingTestCaseId, setEditingTestCaseId] = useState(null);
  const [isViewTestCaseModalOpen, setIsViewTestCaseModalOpen] = useState(false);
  const [isEditTestCaseModalOpen, setIsEditTestCaseModalOpen] = useState(false);
  const [isViewScriptModalOpen, setIsViewScriptModalOpen] = useState(false);
  const [isScriptReadOnly, setIsScriptReadOnly] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [isSavingScript, setIsSavingScript] = useState(false);
  const [selectedTestCaseForScript, setSelectedTestCaseForScript] =
    useState(null);
  const [isRunTestCaseModalOpen, setIsRunTestCaseModalOpen] = useState(false);
  const [selectedTestCaseForRun, setSelectedTestCaseForRun] = useState(null);
  const [testCaseForStepsEdit, setTestCaseForStepsEdit] = useState(null);
  const [compilingTestCaseId, setCompilingTestCaseId] = useState(null);
  const [compilingAllProjectId, setCompilingAllProjectId] = useState(null);
  // Project whose "Compile and Generate" options modal is open
  const [compileOptionsProjectId, setCompileOptionsProjectId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testCaseToDelete, setTestCaseToDelete] = useState(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTestSuiteId, setSelectedTestSuiteId] = useState(null);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isCICDModalOpen, setIsCICDModalOpen] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentProjectPage, setCurrentProjectPage] = useState(1);
  const [totalProjectPages, setTotalProjectPages] = useState(1);
  const [projectsPerPage] = useState(10);
  const [allProjects, setAllProjects] = useState([]);
  const [currentPages, setCurrentPages] = React.useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [projectTypeFilter, setProjectTypeFilter] = useState("");
  // Story filter states
  const [projectStories, setProjectStories] = useState({});
  const [selectedStories, setSelectedStories] = useState({});
  const [storiesLoading, setStoriesLoading] = useState({});

  // Per-project test case pagination and filtering states
  const [projectTestCasePages, setProjectTestCasePages] = useState({});
  const [projectTestCaseTotalPages, setProjectTestCaseTotalPages] = useState(
    {}
  );
  const [projectTestCaseSearchTerms, setProjectTestCaseSearchTerms] = useState(
    {}
  );
  const [projectTestCaseTypeFilters, setProjectTestCaseTypeFilters] = useState(
    {}
  );
  const [projectTestCaseStatusFilters, setProjectTestCaseStatusFilters] =
    useState({});
  const [projectTestCasePriorityFilters, setProjectTestCasePriorityFilters] =
    useState({});
  const [projectSortConfig, setProjectSortConfig] = useState({});
  const [projectTestCasesData, setProjectTestCasesData] = useState({});
  const testCasesPerPage = 10;
  const [recordingWatchToken, setRecordingWatchToken] = useState(0);
  const recordingPendingProjectIdRef = useRef(null);
  const recordingBaselineTotalRef = useRef(0);
  const lastRecordingRefreshRef = useRef(0);

  const RECORDING_REFRESH_EVENT_TYPES = new Set([
    "RECORDING_COMPLETE",
    "TEST_CASE_SAVED",
    "RECORDING_SAVED",
  ]);

  // Route-based pagination for projects
  const [searchParams, setSearchParams] = useSearchParams();
  const getPageFromSearchParams = (searchParams) => {
    const page = parseInt(searchParams.get('page'), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  };
  const projectPageFromRoute = getPageFromSearchParams(searchParams) || 1;

  const setProjectCurrentPage = useCallback((page) => {
    const validPage = Math.max(1, Number(page) || 1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (validPage <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(validPage));
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Healing Modal States
const [isHealingModalOpen, setIsHealingModalOpen] = useState(false);
  const [selectedTestCaseForHealing, setSelectedTestCaseForHealing] =
    useState(null);

  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [accessToken, setAccessToken] = useState("");
  useEffect(() => {
    const authDataString = localStorage.getItem("user");
    if (authDataString) {
      try {
        const authData = JSON.parse(authDataString);
        setAccessToken(authData.accessToken || "");
      } catch (error) {
        console.error("Error parsing auth data:", error);
      }
    }
  }, []);
  const handleNavigateToTestSuite = (suiteId, projectId) => {
    if (!accessToken) {
      console.error("No access token found, redirecting to login");
      navigate("/login");
      return;
    }

    // Navigate with both projectId and suiteId
    navigate(
      `/dashboard/test-suites/details?project_id=${projectId}&suiteId=${suiteId}`
    );
  };
  // Function to show action feedback
  const showActionFeedback = (message, type = "success") => {
    setActionFeedback({ message, type });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setActionFeedback(null);
    }, 3000);
  };

  // Fetch stories for a project
  const fetchProjectStories = async (projectId) => {
    try {
      setStoriesLoading((prev) => ({ ...prev, [projectId]: true }));

      const response = await axiosInstance.get(
        `/jira_data/story_dropdown?project_id=${projectId}`
      );

      if (response && response.status == "success") {
        setProjectStories((prev) => ({
          ...prev,
          [projectId]: response?.stories || [],
        }));
      } else {
        console.error("Failed to fetch stories:", response);
        setProjectStories((prev) => ({
          ...prev,
          [projectId]: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching project stories:", error);
      setProjectStories((prev) => ({
        ...prev,
        [projectId]: [],
      }));
    } finally {
      setStoriesLoading((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  // Refresh the project's latest discovery run id in localStorage for compile script
  const loadDiscoveryRunId = async (projectId) => {
    try {
      await fetchLatestDiscoveryRunId(projectId);
    } catch (error) {
      console.error("Error fetching discovery run id:", error);
    }
  };

  // Removed auto-fetch of stories on component mount
  // Stories will be fetched when project is expanded

  const fetchProjects = async (search = "") => {
    try {
      setProjectsLoading(true);

      const authDataString = localStorage.getItem("user");
      if (!authDataString) throw new Error("User not authenticated");

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) throw new Error("Access token not found");

      const params = {
        page: projectPageFromRoute,
        page_size: projectsPerPage,
      };

      // When a project is selected in the NavBar, search by its id instead of
      // its name so the search doesn't match other projects with similar names
      if (contextProject?.id) {
        params.search = contextProject.id;
      } else if (projectSearchTerm || search) {
        params.search = search || projectSearchTerm;
      }

      if (projectStatusFilter && projectStatusFilter !== "all") {
        params.pro_status = projectStatusFilter;
      }
      params.workspace_id = parseInt(location.pathname.split('/')[1].split('_')[1], 10) || null;


      if (projectTypeFilter) {
        params.project_type = projectTypeFilter;
      }

      const response = await axiosInstance.get(
        `/projects/suite_dashboard_data`,
        {
          params,
        }
      );

      const transformed = response?.projects.map((project) => ({
        id: project.project_id,
        name: project.project_name,
        description: project.description || "No description available",
        type: project.project_type || "Not specified",
        status: project.status || "Unknown",
        total_test_cases: project.total_test_cases || 0,
        total_passed: project.total_passed || 0,
        total_failed: project.total_failed || 0,
        total_pending: project.total_pending || 0,
        pass_rate: project.pass_rate || 0,
      }));

      setAllProjects(transformed);
      setProjects(transformed);
      setFilteredProjects(transformed);

      if (response?.total_pages) {
        setTotalProjectPages(response?.total_pages);
      } else if (response?.total_count) {
        setTotalProjectPages(
          Math.ceil(response?.total_count / projectsPerPage)
        );
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to load projects. Please try again.");
    } finally {
      setProjectsLoading(false);
    }
  };

  // Keep a stable reference to the latest fetchProjects for event listeners
  const fetchProjectsRef = useRef();
  fetchProjectsRef.current = fetchProjects;

  // Refresh dashboard data once the extension reports a cloud test run finished
  useEffect(() => {
    const handleTestProgressMessage = (event) => {
      const msg = event.data;
      if (msg?.source !== "ROBITEST_AGENT") return;
      if (msg.type !== "TEST_PROGRESS") return;

      const isComplete =
        (msg.percent != null && msg.percent >= 100) ||
        (msg.total != null && (msg.completed ?? 0) >= msg.total);
      if (!isComplete) return;

      fetchProjectsRef.current?.();
    };

    window.addEventListener("message", handleTestProgressMessage);
    return () => window.removeEventListener("message", handleTestProgressMessage);
  }, []);

  // Sync with global project context from NavBar
  useEffect(() => {
    if (contextProject && contextProject.id) {
      // Project selected in NavBar
      setProjectSearchTerm(contextProject.name);
      setSearchInputDisabled(true);
      setCurrentProjectPage(1);
      fetchProjects();
      fetchTestCase(contextProject.id, 1, "", null);
      fetchProjectStories(contextProject.id);
      loadDiscoveryRunId(contextProject.id);
      setExpandedProjects((prev) => ({ ...prev, [contextProject.id]: true }));
    } else {
      // setCurrentProjectPage(currentProjectPage);
      setProjectSearchTerm("");
      // No project selected in NavBar
      // fetchProjects();
      setSearchInputDisabled(false);
    }
  }, [
    contextProject,
    projectPageFromRoute,
    projectsPerPage,
    projectStatusFilter,
    projectTypeFilter,
    // projectSearchTerm
  ]);

  useEffect(() => {
    if (!contextProject.id) {    
      const debounceTimer = setTimeout(() => {
      fetchProjects();
          }, 500);
      return () => clearTimeout(debounceTimer);

      }
    }, [projectSearchTerm,projectPageFromRoute,projectStatusFilter, projectTypeFilter]);

  // useEffect(() => {
  //   const debounceTimer = setTimeout(() => {
  //     fetchProjects();

  //   return () => clearTimeout(debounceTimer);
  // }, [currentProjectPage, projectsPerPage, projectSearchTerm, projectStatusFilter, projectTypeFilter]);
  // Added projectSearchTerm to dependencies

  // useEffect(() => {
  //   if (projectSearchTerm) {
  //     const filtered = allProjects.filter(project =>
  //       project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.description.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.type.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.status.toLowerCase().includes(projectSearchTerm.toLowerCase())
  //     );
  //     setFilteredProjects(filtered);
  //      setCurrentProjectPage(1);
  //   } else {
  //     setFilteredProjects(allProjects);
  //   }
  // }, [projectSearchTerm, allProjects]);

  const getPaginatedProjects = () => {
    return projects;
  };

  const handleProjectPageChange = (page) => {
    setProjectCurrentPage(page);
  };

  useEffect(() => {
    if (!projects || projects.length === 0) return;
    setProjectsLoading(false);
  }, [projects]);

  const buildTestCaseFilterParams = (
    projectId,
    searchTerm,
    testCaseType,
    options = {}
  ) => {
    const params = {
      project_id: projectId,
    };

    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    if (
      testCaseType !== null &&
      testCaseType !== undefined &&
      testCaseType !== "" &&
      testCaseType !== "all"
    ) {
      params.test_case_type = parseInt(testCaseType, 10);
    }

    const storyId =
      options.jiraStoryId !== undefined
        ? options.jiraStoryId
        : selectedStories[projectId];
    if (storyId) {
      params.jira_story_id = storyId;
    }

    const testStatus =
      options.testStatus !== undefined
        ? options.testStatus
        : projectTestCaseStatusFilters[projectId];
    if (testStatus && testStatus !== "all") {
      params.test_status = testStatus;
    }

    const priorityFilter =
      options.priorityFilter !== undefined
        ? options.priorityFilter
        : projectTestCasePriorityFilters[projectId];
    if (priorityFilter && priorityFilter !== "all") {
      params.priority_filter = priorityFilter;
    }

    return params;
  };

  const buildTestCaseListParams = (
    projectId,
    page,
    searchTerm,
    testCaseType,
    options = {}
  ) => {
    const params = {
      ...buildTestCaseFilterParams(
        projectId,
        searchTerm,
        testCaseType,
        options
      ),
      page,
      page_size: testCasesPerPage,
    };

    const sortConfig =
      options.sortConfig !== undefined
        ? options.sortConfig
        : projectSortConfig[projectId];
    if (sortConfig?.sort_type) {
      params.sort_type = sortConfig.sort_type;
      params.sort_order = sortConfig.sort_order;
    }

    return params;
  };

  const fetchTestCase = async (
    projectId,
    page = 1,
    searchTerm = "",
    testCaseType = null,
    options = {}
  ) => {
    try {
      setLoadingProjectId(projectId);

      const params = buildTestCaseListParams(
        projectId,
        page,
        searchTerm,
        testCaseType,
        options
      );

      const res = await axiosInstance.get(`/test_case/cases_list`, {
        params,
      });

      console.log("API Response for project", projectId, ":", res.data);

      if (res.data && res.data) {
        const projectCases = res.data.map((tc) => ({
          id: tc.test_case_id,
          name: tc.name,
          status: tc.status,
          priority: tc?.priority?.toLowerCase(),
          suiteId: tc.suite_id,
          suiteName: tc.suite_name,
          project_id: projectId,
          description: tc.description,
          automation_status: tc.automation_status,
          execution_time: tc.execution_time,
          environment: tc.environment,
          environment_id: tc.environment_id,
          browser: tc.browser,
          device: tc.device,
          tags: tc.tags,
          attachments: tc.attachments,
          created_by: tc.created_by,
          created_at: tc.created_at,
          updated_at: tc.updated_at,
          ai_generated: tc.ai_generated,
          is_healed: tc.is_healed,
          test_case_type: tc.test_case_type,

          steps: tc.steps || {
            step_description: [],
            expected_result: [],
            prerequisites: [],
            additional_notes: [],
          },
        }));

        console.log("Mapped test cases with steps:", projectCases);

        // Store test cases data per project
        setProjectTestCasesData((prev) => ({
          ...prev,
          [projectId]: projectCases,
        }));

        // Update pagination info
        const totalPages = Math.ceil((res.total || 0) / testCasesPerPage);
        setProjectTestCaseTotalPages((prev) => ({
          ...prev,
          [projectId]: totalPages,
        }));

        setTotal(res.total || projectCases.length);

        if (res.data.summary) {
          setSummaryCard(res.data.summary);
        }

        return res.total || projectCases.length;
      } else {
        console.log("No test cases data found in response");
        setProjectTestCasesData((prev) => ({
          ...prev,
          [projectId]: [],
        }));
        setProjectTestCaseTotalPages((prev) => ({
          ...prev,
          [projectId]: 1,
        }));
        setTotal(0);
        return 0;
      }
    } catch (err) {
      console.error("Error loading test cases:", err);
      setError("Unable to load test cases. Please try again.");
      setProjectTestCasesData((prev) => ({
        ...prev,
        [projectId]: [],
      }));
      return null;
    } finally {
      setLoadingProjectId(null);
    }
  };

  useEffect(() => {
    let filtered = [...testCases];

    if (testCaseSearchTerm) {
      filtered = filtered.filter((testCase) => {
        if (
          testCase.name
            .toLowerCase()
            .includes(testCaseSearchTerm.toLowerCase()) ||
          testCase.id.toString().includes(testCaseSearchTerm)
        ) {
          return testCase;
        } else {
          return null;
        }
      });
    }

    if (projectFilter !== "all") {
      filtered = filtered.filter((testCase) => {
        return testCase.project_id.toString() === projectFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "id":
          comparison = a.id - b.id;
          break;
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        case "priority":
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        default:
          comparison = a.id - b.id;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    setFilteredTestCases(filtered);
  }, [
    testCases,
    testCaseSearchTerm,
    projectFilter,
    sortBy,
    sortOrder,
  ]);

  //   useEffect(() => {
  //   let filtered = [...allProjects];
  //     if (projectSearchTerm) {
  //     filtered = filtered.filter(project =>
  //       project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.description.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.type.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
  //       project.status.toLowerCase().includes(projectSearchTerm.toLowerCase())
  //     );
  //   }
  //     if (statusFilter !== "all") {
  //     filtered = filtered.filter(project =>
  //       project.status.toLowerCase() === statusFilter.toLowerCase()
  //     );
  //   }
  //     setFilteredProjects(filtered);
  //  }, [projectSearchTerm, statusFilter, allProjects]);

  // Calculate statistics
  const calculateStats = () => {
    const total = testCases.length;
    const passed = testCases.filter((tc) => tc.status === "passed").length;
    const failed = testCases.filter((tc) => tc.status === "failed").length;
    const pending = testCases.filter((tc) => tc.status === "pending").length;

    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

    return { total, passed, failed, pending, passRate };
  };

  const updateProjectStatsAfterDelete = (projectId, deletedStatus) => {
    const updateProject = (project) => {
      if (project.id !== projectId) return project;

      const total = Math.max(0, (project.total_test_cases || 0) - 1);
      let passed = project.total_passed || 0;
      let failed = project.total_failed || 0;
      let pending = project.total_pending || 0;

      const status = (deletedStatus || "").toLowerCase();
      if (status === "passed") {
        passed = Math.max(0, passed - 1);
      } else if (status === "failed") {
        failed = Math.max(0, failed - 1);
      } else {
        pending = Math.max(0, pending - 1);
      }

      const passRate =
        total > 0
          ? parseFloat(((passed / total) * 100).toFixed(2))
          : 0;

      return {
        ...project,
        total_test_cases: total,
        total_passed: passed,
        total_failed: failed,
        total_pending: pending,
        pass_rate: passRate,
      };
    };

    setProjects((prev) => prev.map(updateProject));
    setAllProjects((prev) => prev.map(updateProject));
    setFilteredProjects((prev) => prev.map(updateProject));
  };

  const syncProjectTestCaseCount = (projectId, totalCount) => {
    if (!projectId || totalCount == null) return;

    const updateProject = (project) => {
      if (project.id !== projectId) return project;
      return { ...project, total_test_cases: totalCount };
    };

    setProjects((prev) => prev.map(updateProject));
    setAllProjects((prev) => prev.map(updateProject));
    setFilteredProjects((prev) => prev.map(updateProject));
  };

  const refreshProjectTestCasesAfterCreate = async (projectId) => {
    if (!projectId) return;

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";

    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

    const totalCount = await fetchTestCase(
      projectId,
      1,
      searchTerm,
      testCaseType === "all" ? null : testCaseType
    );

    if (totalCount != null) {
      syncProjectTestCaseCount(projectId, totalCount);
    }

    return totalCount;
  };

  const refreshAfterRecordingRef = useRef(refreshProjectTestCasesAfterCreate);
  refreshAfterRecordingRef.current = refreshProjectTestCasesAfterCreate;

  // Calculate project-specific statistics
  const calculateProjectStats = (projectId) => {
    const projectTestCases = testCases.filter((tc) => {
      if (tc.project_id === projectId) {
        return tc;
      } else {
        return;
      }
    });

    const total = projectTestCases.length;
    const passed = projectTestCases.filter(
      (tc) => tc.status === "passed"
    ).length;
    const failed = projectTestCases.filter(
      (tc) => tc.status === "failed"
    ).length;
    const pending = projectTestCases.filter(
      (tc) => tc.status === "pending"
    ).length;

    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

    return { total, passed, failed, pending, passRate };
  };

  // Toggle project expansion
  const toggleProjectExpansion = async (projectId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!expandedProjects[projectId]) {
      // Reset all filters when expanding project
      setSelectedStories((prev) => ({ ...prev, [projectId]: "" }));
      setProjectTestCaseSearchTerms((prev) => ({ ...prev, [projectId]: "" }));
      setProjectTestCaseTypeFilters((prev) => ({
        ...prev,
        [projectId]: "all",
      }));
      setProjectTestCaseStatusFilters((prev) => ({
        ...prev,
        [projectId]: "all",
      }));
      setProjectTestCasePriorityFilters((prev) => ({
        ...prev,
        [projectId]: "",
      }));
      setProjectSortConfig((prev) => ({ ...prev, [projectId]: null }));
      setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

      // Fetch stories for this project if not already fetched
      if (!projectStories[projectId]) {
        await fetchProjectStories(projectId);
      }

      loadDiscoveryRunId(projectId);

      // Fetch test cases with page 1
      await fetchTestCase(projectId, 1, "", null);
    } else {
      // Reset all filters when collapsing project
      setSelectedStories((prev) => ({ ...prev, [projectId]: "" }));
      setProjectTestCaseSearchTerms((prev) => ({ ...prev, [projectId]: "" }));
      setProjectTestCaseTypeFilters((prev) => ({
        ...prev,
        [projectId]: "all",
      }));
      setProjectTestCaseStatusFilters((prev) => ({
        ...prev,
        [projectId]: "all",
      }));
      setProjectTestCasePriorityFilters((prev) => ({
        ...prev,
        [projectId]: "",
      }));
      setProjectSortConfig((prev) => ({ ...prev, [projectId]: null }));
      setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));
    }

    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Handle story filter change
  const handleStoryFilterChange = async (projectId, storyId) => {
    setSelectedStories((prev) => ({ ...prev, [projectId]: storyId }));
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    await fetchTestCase(
      projectId,
      1,
      searchTerm,
      testCaseType === "all" ? null : testCaseType,
      { jiraStoryId: storyId }
    );
  };

  const handleTestCaseStatusFilterChange = async (projectId, statusValue) => {
    setProjectTestCaseStatusFilters((prev) => ({
      ...prev,
      [projectId]: statusValue,
    }));
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    await fetchTestCase(
      projectId,
      1,
      searchTerm,
      testCaseType === "all" ? null : testCaseType,
      { testStatus: statusValue }
    );
  };

  const handleTestCasePriorityFilterChange = async (projectId, priorityValue) => {
    setProjectTestCasePriorityFilters((prev) => ({
      ...prev,
      [projectId]: priorityValue,
    }));
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    await fetchTestCase(
      projectId,
      1,
      searchTerm,
      testCaseType === "all" ? null : testCaseType,
      { priorityFilter: priorityValue }
    );
  };

  // Handle test case search with debouncing
  const handleTestCaseSearch = async (projectId, searchTerm) => {
    setProjectTestCaseSearchTerms((prev) => ({
      ...prev,
      [projectId]: searchTerm,
    }));
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));
  };

  // Debounced API call for search
  useEffect(() => {
    const debounceTimers = {};

    return () => {
      Object.values(debounceTimers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Handle test case type filter change
  const handleTestCaseTypeFilterChange = async (projectId, typeValue) => {
    setProjectTestCaseTypeFilters((prev) => ({
      ...prev,
      [projectId]: typeValue,
    }));
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: 1 }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    await fetchTestCase(
      projectId,
      1,
      searchTerm,
      typeValue === "all" ? null : typeValue
    );
  };

  // Handle test case pagination
  const handleTestCasePageChange = async (projectId, page) => {
    setProjectTestCasePages((prev) => ({ ...prev, [projectId]: page }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    await fetchTestCase(
      projectId,
      page,
      searchTerm,
      testCaseType === "all" ? null : testCaseType
    );
  };

  const handleRefreshTestCases = async (projectId) => {
    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    const currentPage = projectTestCasePages[projectId] || 1;
    await fetchTestCase(
      projectId,
      currentPage,
      searchTerm,
      testCaseType === "all" ? null : testCaseType
    );
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(async () => {
      for (const [projectId, searchTerm] of Object.entries(
        projectTestCaseSearchTerms
      )) {
        if (expandedProjects[projectId]) {
          const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
          await fetchTestCase(
            parseInt(projectId),
            1,
            searchTerm,
            testCaseType === "all" ? null : testCaseType
          );
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [projectTestCaseSearchTerms]);

  const handleCreateTestCaseManuallyClick = () => {
    // Show options modal instead of navigating
    setIsCreateTestCaseOptionsModalOpen(true);
  };

  // Handle creating test case manually (from options modal)
  const handleCreateTestCaseOption = () => {
    setSelectedTestSuiteId(null);
    setIsCreateTestCaseModalOpen(true);
  };

  // Handle creating test case via extension (from options modal)
  const handleCreateTestCaseViaExtension = () => {
    setExtensionRecordingModalMode("create");
    setEditingTestCaseId(null);
    setIsExtensionRecordingModalOpen(true);
  };

  // Manual / AI generated: edit test steps (UI Discovery flow)
  // Recorded: edit test case env details via ExtensionRecordingModal
  const handleEditTestCaseFromList = (testCase, project) => {
    if (usesDiscoveryFlow(testCase)) {
      setTestCaseForStepsEdit(testCase);
      return;
    }

    setSelectedProjectId(project?.id || testCase?.project_id || null);
    setEditingTestCaseId(testCase?.id || testCase?.test_case_id || null);
    setExtensionRecordingModalMode("edit");
    setIsExtensionRecordingModalOpen(true);
  };

  const handleCloseExtensionRecordingModal = () => {
    setIsExtensionRecordingModalOpen(false);
    setExtensionRecordingModalMode("create");
    setEditingTestCaseId(null);
  };

  const navigateToProjectCrawl = (projectId) => {
    const subPath = `project-crawl/${projectId}`;
    const workspaceId = getWorkspaceIdFromPath(location.pathname);
    navigate(
      workspaceId
        ? buildWorkspaceDashboardPath(workspaceId, projectId, subPath)
        : `/dashboard/${subPath}`
    );
  };

  // Uses the run id cached in localStorage, refetching it in case the project
  // was crawled since. Sends the user to crawl the project when it has no run.
  const resolveDiscoveryRunId = async (projectId) => {
    let runId = getStoredDiscoveryRunId(projectId);
    if (!runId) {
      try {
        runId = await fetchLatestDiscoveryRunId(projectId);
      } catch (error) {
        console.error("Error fetching discovery run id:", error);
        showActionFeedback("Failed to fetch UI discovery run. Please try again.", "error");
        return null;
      }
    }

    if (!runId) {
      toast.info("Run UI discovery for this project before generating scripts.");
      navigateToProjectCrawl(projectId);
      return null;
    }

    return runId;
  };

  const handleCompileScript = async (testCase) => {
    const projectId = testCase.project_id;
    setCompilingTestCaseId(testCase.id);
    try {
      const runId = await resolveDiscoveryRunId(projectId);
      if (!runId) return;

      showActionFeedback(`Generating script for "${testCase.name}"...`, "info");
      await axiosInstance.post(
        "/crawler/generate_cwral_script",
        { discovery_run_id: runId },
        { params: { test_case_id: testCase.id } }
      );
      showActionFeedback(`Script generated for "${testCase.name}"`, "success");
      await handleRefreshTestCases(projectId);
    } catch (error) {
      console.error("Error compiling automation script:", error);
      showActionFeedback(
        error.response?.data?.message || "Failed to compile script. Please try again.",
        "error"
      );
    } finally {
      setCompilingTestCaseId(null);
    }
  };

  // Starts background script generation for the project's manual (1) or AI
  // generated (3) test cases; a null testCaseType compiles both. API errors are
  // rethrown so CompileScriptsOptionsModal shows them and the user can retry.
  const handleCompileAllScripts = async (projectId, testCaseType) => {
    setCompilingAllProjectId(projectId);
    try {
      const runId = await resolveDiscoveryRunId(projectId);
      if (!runId) {
        setCompileOptionsProjectId(null);
        return;
      }

      const payload = { discovery_run_id: runId };
      if (testCaseType) payload.test_case_type = testCaseType;
      const response = await axiosInstance.post(
        "/crawler/generate/crwal/project/script",
        payload,
        { params: { project_id: projectId } }
      );
      setCompileOptionsProjectId(null);
      showActionFeedback(response?.message || SCRIPT_GENERATION_STARTED_MESSAGE, "success");
      refreshNotifications();
    } catch (error) {
      // A stale cached run id gives 404; refresh it so a retry uses the latest run
      if (error?.response?.status === 404) {
        fetchLatestDiscoveryRunId(projectId).catch((e) =>
          console.error("Error fetching discovery run id:", e)
        );
      }
      throw error;
    } finally {
      setCompilingAllProjectId(null);
    }
  };

  const handleTestCaseEnvUpdated = async (projectId) => {
    showActionFeedback("Test case updated successfully", "success");

    if (!projectId) return;

    const currentPage = projectTestCasePages[projectId] || 1;
    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";

    await fetchTestCase(
      projectId,
      currentPage,
      searchTerm,
      testCaseType === "all" ? null : testCaseType
    );
  };

  const handleRecordingStarted = (projectId) => {
    if (!projectId) return;

    recordingPendingProjectIdRef.current = projectId;
    const project = projects.find((p) => p.id === projectId);
    recordingBaselineTotalRef.current =
      project?.total_test_cases ??
      projectTestCasesData[projectId]?.length ??
      0;
    setRecordingWatchToken((token) => token + 1);
  };

  const tryRefreshAfterRecording = useCallback(async () => {
    const projectId = recordingPendingProjectIdRef.current;
    if (!projectId || document.visibilityState !== "visible") return;

    const now = Date.now();
    if (now - lastRecordingRefreshRef.current < 2000) return;
    lastRecordingRefreshRef.current = now;

    const totalCount = await refreshAfterRecordingRef.current?.(projectId);

    if (
      totalCount != null &&
      totalCount > recordingBaselineTotalRef.current
    ) {
      recordingPendingProjectIdRef.current = null;
    }
  }, []);

  // Refresh list when user returns after extension recording
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      tryRefreshAfterRecording();
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [tryRefreshAfterRecording]);

  // Refresh immediately if extension reports recording completion
  useEffect(() => {
    const handleAgentMessage = (event) => {
      const msg = event.data;
      if (msg?.source !== "ROBITEST_AGENT") return;
      if (!RECORDING_REFRESH_EVENT_TYPES.has(msg.type)) return;

      const projectId =
        msg.project_id || msg.projectId || recordingPendingProjectIdRef.current;
      if (!projectId) return;

      recordingPendingProjectIdRef.current = projectId;
      tryRefreshAfterRecording();
    };

    window.addEventListener("message", handleAgentMessage);
    return () => window.removeEventListener("message", handleAgentMessage);
  }, [tryRefreshAfterRecording]);

  // Refresh dashboard counts when the extension reports recording stopped successfully
  useEffect(() => {
    const handleRecordingStoppedMessage = (event) => {
      const msg = event.data;
      if (msg?.type !== "RECORDING_STOPPED") return;
      if (msg.success !== true) return;

      // Refresh the project cards / counts
      fetchProjectsRef.current?.();

      // Refresh the expanded project's test case list if we can resolve it
      const projectId =
        msg.projectId ||
        msg.project_id ||
        recordingPendingProjectIdRef.current;
      if (projectId) {
        recordingPendingProjectIdRef.current = projectId;
        tryRefreshAfterRecording();
      }
    };

    window.addEventListener("message", handleRecordingStoppedMessage);
    return () =>
      window.removeEventListener("message", handleRecordingStoppedMessage);
  }, [tryRefreshAfterRecording]);

  // Close test case actions dropdown when clicking outside
  useEffect(() => {
    if (activeActionDropdown === null) return undefined;

    const handleClickOutside = (event) => {
      if (!event.target.closest(".test-case-actions")) {
        setActiveActionDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeActionDropdown]);

  // Clear stale recording refresh flag after 30 minutes
  useEffect(() => {
    if (!recordingPendingProjectIdRef.current) return undefined;

    const timeoutId = setTimeout(() => {
      recordingPendingProjectIdRef.current = null;
    }, 30 * 60 * 1000);

    return () => clearTimeout(timeoutId);
  }, [recordingWatchToken]);

  // Group test cases by project
  const getProjectTestCases = (projectId) => {
    return projectTestCasesData[projectId] || [];
  };

  // Handle creating a new test case
  const handleCreateTestCase = async (newTestCase) => {
    const projectId = selectedProjectId || newTestCase.project_id;
    const updatedTestCase = {
      ...newTestCase,
      id: Math.max(...testCases.map((tc) => tc.id), 0) + 1,
      projectId: selectedProjectId,
      projectName: projects.find((p) => p.id === selectedProjectId)?.name || "",
      suiteId: selectedTestSuiteId,

      suiteName: "New Test Suite",
    };

    setTestCases([...testCases, updatedTestCase]);

    if (projectId) {
      try {
        await refreshProjectTestCasesAfterCreate(projectId);
      } catch (error) {
        console.error("Error refreshing test cases after creation:", error);
      }
    }

    // Show success message
    showActionFeedback(
      `Test case "${updatedTestCase.name}" created successfully`,
      "success"
    );
  };

  // Handle viewing a test case — prefer standalone detail page
  const handleViewTestCase = (testCase, projectName) => {
    const caseId = testCase?.id || testCase?.test_case_id;
    const projectId =
      testCase?.project_id ||
      getProjectIdFromPath(location.pathname) ||
      contextProject?.id;
    const workspaceId = getWorkspaceIdFromPath(location.pathname);

    if (caseId && workspaceId) {
      const path = buildWorkspaceDashboardPath(
        workspaceId,
        projectId,
        `test-cases/${caseId}?project_id=${projectId || ''}`
      );
      navigate(path, { state: { testCase: { ...testCase, projectName } } });
      return;
    }

    if (caseId) {
      navigate(`/dashboard/test-cases/${caseId}?project_id=${projectId || ''}`, {
        state: { testCase: { ...testCase, projectName } },
      });
      return;
    }

    testCase["projectName"] = projectName;
    setSelectedTestCase(testCase);
    setIsViewTestCaseModalOpen(true);
  };

  const handleTestExecutionComplete = async (projectId) => {
    if (!projectId) return;

    try {
      const currentPage = projectTestCasePages[projectId] || 1;
      const searchTerm = projectTestCaseSearchTerms[projectId] || "";
      const typeFilter = projectTestCaseTypeFilters[projectId] || null;
      await fetchTestCase(projectId, currentPage, searchTerm, typeFilter);
    } catch (error) {
      console.error("Error refreshing test cases after execution:", error);
    }
  };

  // Handle viewing test case script (read-only from actions menu)
  const handleViewScript = async (testCase) => {
    setSelectedTestCaseForScript(testCase);
    setIsScriptReadOnly(true);
    setIsViewScriptModalOpen(true);
    setEditorContent("// Loading script...");
    try {
      if (usesDiscoveryFlow(testCase)) {
        const response = await axiosInstance.get(
          `/crawlerdiscovery_test/script?test_case_id=${testCase.id}`
        );
        const data = response?.data || response;
        const content =
          typeof data === "string"
            ? data
            : data?.generated_code ||
              data?.script?.generated_code ||
              JSON.stringify(data, null, 2);
        setEditorContent(content || "// No script available");
        return;
      }

      const response = await axiosInstance.get(
        `/execute_file/get_automation_script/${testCase.id}`
      );
      setEditorContent(response || "// No script available");
    } catch (error) {
      console.error("Error fetching automation script:", error);
      setEditorContent("// Error loading automation script");
      showActionFeedback("Failed to load automation script", "error");
    }
  };

  const closeScriptModal = () => {
    setIsViewScriptModalOpen(false);
    setSelectedTestCaseForScript(null);
    setIsScriptReadOnly(false);
  };

  const handleRunTestCaseFromList = (testCase, projectName) => {
    setSelectedTestCaseForRun({ ...testCase, projectName });
    setIsRunTestCaseModalOpen(true);
  };

  const closeRunTestCaseModal = () => {
    setIsRunTestCaseModalOpen(false);
    setSelectedTestCaseForRun(null);
  };

  // Stable callback so RunDiscoveryTestCaseModal keeps its agent listener
  // while a local run is in progress
  const discoveryRunCompleteRef = useRef(null);
  discoveryRunCompleteRef.current = () =>
    handleTestExecutionComplete(selectedTestCaseForRun?.project_id);
  const handleDiscoveryRunComplete = useCallback(
    () => discoveryRunCompleteRef.current?.(),
    []
  );

  const handleDeleteTestCaseFromList = (testCase) => {
    setTestCaseToDelete(testCase);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTestCase = async () => {
    if (!testCaseToDelete) return;

    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/test_case/${testCaseToDelete.id}`);
      await handleDeleteTestCase(
        testCaseToDelete.id,
        testCaseToDelete.project_id
      );
      setShowDeleteConfirm(false);
      setTestCaseToDelete(null);
    } catch (error) {
      console.error("Error deleting test case:", error);
      showActionFeedback("Failed to delete test case. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle saving test case script
  const handleSaveScript = async () => {
    // if (!selectedTestCaseForScript || !selectedTestCaseForScript.id) {
    //   showActionFeedback('No test case selected', 'error');
    //   return;
    // }

    setIsSavingScript(true);
    try {
      const testCaseId =
        selectedTestCaseForScript?.id || selectedTestCase?.id;
      const response = await axiosInstance.put(
        `/execute_file/test-cases/${testCaseId}/update_script`,
        editorContent,
        {
          headers: {
            "Content-Type": "text/plain",
          },
        }
      );

      if (response.status === "success" || response.status === 200) {
        showActionFeedback("Script saved successfully!", "success");
        setIsViewScriptModalOpen(false);
      } else {
        throw new Error(response.message || "Failed to save script");
      }
    } catch (error) {
      console.error("Error saving script:", error);
      showActionFeedback(
        error.response?.data?.message ||
          error.message ||
          "Failed to save script",
        "error"
      );
    } finally {
      setIsSavingScript(false);
    }
  };

  // Handle editing a test case
  const handleEditTestCase = (testCaseId) => {
    // const testCase = testCases.find((tc) => tc.id === testCaseId);
    // if (testCase) {
      // setSelectedTestCase(testCase);
      setIsEditTestCaseModalOpen(true);
      setIsViewTestCaseModalOpen(false);
    // }
  };

  // Handle saving an edited test case
  const handleSaveTestCase = (updatedTestCase) => {
    // Update the test case in the state
    const updatedTestCases = testCases.map((tc) =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);

    // Show success message
    showActionFeedback(
      `Test case "${updatedTestCase.name}" updated successfully`,
      "success"
    );
  };

  // Handle deleting a test case
  const handleDeleteTestCase = async (testCaseId, projectId) => {
    const deletedTestCase =
      (testCaseToDelete?.id === testCaseId && testCaseToDelete) ||
      (selectedTestCase?.id === testCaseId && selectedTestCase) ||
      projectTestCasesData[projectId]?.find((tc) => tc.id === testCaseId) ||
      testCases.find((tc) => tc.id === testCaseId);

    if (projectId) {
      updateProjectStatsAfterDelete(projectId, deletedTestCase?.status);

      setProjectTestCasesData((prev) => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter(
          (tc) => tc.id !== testCaseId
        ),
      }));
    }

    const updatedTestCases = testCases.filter((tc) => tc.id !== testCaseId);
    setTestCases(updatedTestCases);

    // Refresh test cases for the project where deletion occurred
    if (projectId) {
      try {
        const currentPage = projectTestCasePages[projectId] || 1;
        const searchTerm = projectTestCaseSearchTerms[projectId] || "";
        const typeFilter = projectTestCaseTypeFilters[projectId] || null;
        await fetchTestCase(projectId, currentPage, searchTerm, typeFilter);
      } catch (error) {
        console.error("Error refreshing test cases after deletion:", error);
      }
    }

    setIsViewTestCaseModalOpen(false);
    showActionFeedback(`Test case deleted successfully`, "success");
  };

  // Handle applying AI suggestions to a test case
  const handleApplySuggestion = (suggestion, testCaseId) => {
    // Find the test case to update
    const testCaseToUpdate = testCases.find((tc) => tc.id === testCaseId);

    if (!testCaseToUpdate) return;

    // Create an updated test case with the suggestion applied
    const updatedTestCase = {
      ...testCaseToUpdate,
      description: testCaseToUpdate.description
        ? `${testCaseToUpdate.description}\n\nApplied suggestion: ${suggestion.description}`
        : `Applied suggestion: ${suggestion.description}`,
    };

    // Update the test case in the state
    const updatedTestCases = testCases.map((tc) =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);

    // Show success message
    showActionFeedback(
      `Applied AI suggestion to test case "${testCaseToUpdate.name}"`,
      "success"
    );
  };

  // Handle applying auto-updates to a test case
  const handleApplyUpdates = (updateData, testCaseId) => {
    // Find the test case to update
    const testCaseToUpdate = testCases.find((tc) => tc.id === testCaseId);

    if (!testCaseToUpdate) return;

    const updatedTestCase = {
      ...testCaseToUpdate,
      description: `${testCaseToUpdate.description || ""}\n\nUpdated with ${
        updateData.changes.length
      } changes. Reason: ${updateData.reason}`,
      version:
        updateData.versionType === "major"
          ? "2.0"
          : updateData.versionType === "minor"
          ? "1.1"
          : "1.0.1",
    };

    // Update the test case in the state
    const updatedTestCases = testCases.map((tc) =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);

    // Show success message
    showActionFeedback(
      `Applied ${updateData.changes.length} updates to test case "${testCaseToUpdate.name}"`,
      "success"
    );
  };

  // Handle submitting a review for a test case
  const handleSubmitReview = (reviewData, testCaseId) => {
    // Find the test case to update
    const testCaseToUpdate = testCases.find((tc) => tc.id === testCaseId);

    if (!testCaseToUpdate) return;

    // Create an updated test case with the review information
    const updatedTestCase = {
      ...testCaseToUpdate,
      reviewStatus:
        reviewData.action === "review"
          ? reviewData.status
          : testCaseToUpdate.reviewStatus || "pending",
      reviewers:
        reviewData.action === "assign"
          ? (testCaseToUpdate.reviewers || []).concat(
              reviewData.reviewers.map((r) => r.id)
            )
          : testCaseToUpdate.reviewers || [],
    };

    // Update the test case in the state
    const updatedTestCases = testCases.map((tc) =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);

    // Show success message
    if (reviewData.action === "assign") {
      showActionFeedback(
        `Assigned ${reviewData.reviewers.length} reviewers to test case "${testCaseToUpdate.name}"`,
        "success"
      );
    } else {
      showActionFeedback(
        `Submitted ${reviewData.status} review for test case "${testCaseToUpdate.name}"`,
        "success"
      );
    }
  };

  const handleCICDClick = () => {
    // if (!projectFilter || projectFilter === "all") {
    //   showActionFeedback("Please select a project first", "error");
    //   return;
    // }

    setSelectedProjectId(projectFilter);
    setIsCICDModalOpen(true);
  };

  // Handle running a test case
  const handleRunTestCase = async (testCaseId) => {
    // In a real application, this would trigger the test execution
    try {
      showActionFeedback(`Running test case #${testCaseId}...`, "info");

      const response = await axiosInstance.post(
        `/execute_file/test-case/execute?test_case_id=${testCaseId}`
      );

      if (response.status === "success") {
        showActionFeedback(
          `${response.message} - Test case ${testCaseId} executed successfully`,
          "success"
        );

        // Optionally display execution output
        // if (response.output) {
        //   console.log('Test execution output:', response.data.output);
        // }

        // Refresh test cases list to show updated status
        // fetchTestCases();
      } else {
        throw new Error(response.message || "Test execution failed");
      }
    } catch (error) {
      console.error("Error running test case:", error);
      showActionFeedback(
        error.response?.message || `Failed to execute test case #${testCaseId}`,
        "error"
      );
    }
  };

  const handleImportClick = () => {
    if (!contextProject?.id) {
      showActionFeedback("Please select a project first", "error");
      return;
    }
    setIsImportModalOpen(true);
  };

  const handleImportSuccess = async (projectId, response) => {
    setIsImportModalOpen(false);
    showActionFeedback(
      response?.message || "Test cases imported successfully",
      "success"
    );

    fetchProjectsRef.current?.();
    if (projectId && expandedProjects[projectId]) {
      try {
        await refreshProjectTestCasesAfterCreate(projectId);
      } catch (error) {
        console.error("Error refreshing test cases after import:", error);
      }
    }
  };

  // Handle export button click — downloads file from export API
  const handleExportClick = async () => {
    const projectId =
      contextProject?.id ||
      selectedProjectId ||
      (projectFilter !== "all" ? projectFilter : null);

    if (!projectId) {
      showActionFeedback("Please select a project first", "error");
      return;
    }

    try {
      setIsExporting(true);
      const searchTerm = projectTestCaseSearchTerms[projectId] || "";
      const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
      const params = buildTestCaseFilterParams(
        projectId,
        searchTerm,
        testCaseType === "all" ? null : testCaseType
      );

      const blobData = await axiosInstance.get(
        `/test_case/export_test_cases`,
        {
          params,
          responseType: "blob",
        }
      );

      const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const mimeToExt = {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          "xlsx",
        "application/vnd.ms-excel": "xls",
        "application/pdf": "pdf",
        "application/json": "json",
        "application/zip": "zip",
        "text/csv": "csv",
      };
      const ext = mimeToExt[blob.type] || "xlsx";
      link.setAttribute(
        "download",
        `test-cases-export-${new Date().toISOString().split("T")[0]}.${ext}`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showActionFeedback("Exported successfully", "success");
    } catch (error) {
      console.error("Export failed:", error);
      showActionFeedback("Failed to export. Please try again.", "error");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle export action (modal — kept for format-based export if re-enabled)
  const handleExport = (exportData) => {
    showActionFeedback(
      `Exported ${
        exportData.items.length
      } test cases in ${exportData.format.toUpperCase()} format`,
      "success"
    );
  };

  // Handle pushing test suites to automation frameworks
  const handlePushToFramework = async (data) => {
    try {
      showActionFeedback(`Pushing test suites to ${data.framework}...`, "info");

      console.log("Pushing to automation framework:", data);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Show success message
      showActionFeedback(
        `Successfully pushed ${data.testSuites.length} test suites to ${
          data.framework === "selenium" ? "Selenium" : "Robot Framework"
        }`,
        "success"
      );
    } catch (error) {
      console.error("Error pushing to framework:", error);
      showActionFeedback(
        "Failed to push test suites. Please try again.",
        "error"
      );
    }
  };

  // Handle test case selection for export
  const handleTestCaseSelection = (testCaseId, isSelected) => {
    setSelectedTestCases((prev) => {
      if (isSelected) {
        return [...prev, testCaseId];
      } else {
        return prev.filter((id) => id !== testCaseId);
      }
    });
  };

  const handleSortChange = async (projectId, column) => {
    const sortTypeMap = {
      name: 1,
      environment: 2,
      id: 3,
    };

    if (!sortTypeMap[column]) return;

    let direction = "asc";
    const current = projectSortConfig[projectId];
    if (current?.key === column && current?.direction === "asc") {
      direction = "desc";
    }

    const newSortConfig = {
      key: column,
      direction,
      sort_type: sortTypeMap[column],
      sort_order: direction === "asc" ? 1 : 2,
    };

    setProjectSortConfig((prev) => ({
      ...prev,
      [projectId]: newSortConfig,
    }));

    const searchTerm = projectTestCaseSearchTerms[projectId] || "";
    const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
    const page = projectTestCasePages[projectId] || 1;
    await fetchTestCase(
      projectId,
      page,
      searchTerm,
      testCaseType === "all" ? null : testCaseType,
      { sortConfig: newSortConfig }
    );
  };

  const renderSortIndicator = (projectId, field) => {
    const config = projectSortConfig[projectId];
    if (!config || config.key !== field) return null;

    return (
      <span className="sort-indicator">
        {config.direction === "asc" ? " ↑" : " ↓"}
      </span>
    );
  };

  /**
   * Handle opening the healing modal
   * Only opens if the test case has is_healed === true
   */
  const handleOpenHealingModal = (testCase) => {    
    if (testCase.is_healed === true) {
      setSelectedTestCaseForHealing(testCase);
      setIsHealingModalOpen(true);
    }
  };

  /**
   * Handle healing completion
   * Refreshes the test cases for the current project to update is_healed status
   */
  const handleHealingComplete = async () => {
    showActionFeedback(
      "✅ Test case healing approved successfully!",
      "success"
    );
    // Refresh test cases for the expanded projects
    const expandedProjectIds = Object.keys(expandedProjects).filter(
      (projectId) => expandedProjects[projectId]
    );

    for (const projectId of expandedProjectIds) {
      const searchTerm = projectTestCaseSearchTerms[projectId] || "";
      const testCaseType = projectTestCaseTypeFilters[projectId] || "all";
      await fetchTestCase(
        parseInt(projectId),
        projectTestCasePages[projectId] || 1,
        searchTerm,
        testCaseType === "all" ? null : testCaseType
      );
    }
  };

  const paginate = (items, pageNumber, pageSize) => {
    const startIndex = (pageNumber - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  };

  // if (projectsLoading) {
  //   return (
  //     <div className="loading-container">
  //       <div className="loading-spinner"></div>
  //       <p>Loading projects...</p>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button
          className="navbar-button primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="test-cases-overview-container">
      {/* Action Feedback */}
      {actionFeedback && (
        <div className={`action-feedback ${actionFeedback.type}`}>
          <span>{actionFeedback.message}</span>
          <button onClick={() => setActionFeedback(null)}>×</button>
        </div>
      )}

      <div
        className="test-cases-overview-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "1rem",
        }}
      >
        <div>
          <h1 className="test-cases-overview-title">Test Cases Overview</h1>
          <p className="test-cases-overview-subtitle">
            View and manage all test cases across projects
          </p>
        </div>
        <div
          className="test-cases-actions"
          style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
        >
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {/* <button
              className="navbar-button"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#333",
                fontWeight: "500",
              }}
              onClick={handleCreateTestCaseManuallyClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
              </svg>
              <span style={{ color: "#333", fontWeight: "500" }}>
                + Create Test Case
              </span>
            </button> */}

            <button
              className="navbar-button"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#333",
                fontWeight: "500",
              }}
              title="Import test cases from an Excel sheet"
              onClick={handleImportClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span style={{ color: "#333", fontWeight: "500" }}>Import</span>
            </button>

            <button
              className="navbar-button"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: isExporting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#333",
                fontWeight: "500",
                opacity: isExporting ? 0.7 : 1,
              }}
              disabled={isExporting}
              title="Export test cases"
              onClick={handleExportClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span style={{ color: "#333", fontWeight: "500" }}>
                {isExporting ? "Exporting..." : "Export"}
              </span>
            </button>

            <button
              className="navbar-button"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#999",
                fontWeight: "500",
                opacity: 0.7,
              }}
              disabled
              title="Coming Soon"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
              </svg>
              <span style={{ color: "#999", fontWeight: "500" }}>
                DevOps Integration
              </span>
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  cursor: "help",
                }}
                title="Coming Soon"
              >
                *
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {/* <div className="test-cases-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search projects..."
            value={projectSearchTerm}
            onChange={(e) => setProjectSearchTerm(e.target.value)}
            disabled={searchInputDisabled}
            className={`search-input ${searchInputDisabled ? "disabled" : ""}`}
          />
          <svg
            className="search-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          {projectsLoading && (
            <div className="search-loading">
              <div className="spinner-small"></div>
            </div>
          )}
        </div> */}

        {/* <div className="filter-group">
          <label>Project Type:</label>
          <select
            value={projectTypeFilter}
            onChange={(e) => setProjectTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="WEB">Web</option>
            <option value="MOBILE">Mobile</option>
            <option value="DESKTOP">Desktop</option>
          </select>
        </div> */}

        {/* <div className="filter-group">
          <label>Status:</label>
          <select
            value={projectStatusFilter}
            onChange={(e) => setProjectStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div> */}
      {/* </div> */}

      {/* Projects and Test Cases */}
      <div className="projects-list">
        {getPaginatedProjects().map((project) => {
          const projectTestCases = getProjectTestCases(project.id);

          if (
            projectTestCases.length === 0 &&
            projectFilter !== "all" &&
            projectFilter !== project.id.toString()
          ) {
            return null;
          }

          const projectStats = calculateProjectStats(project.id);

          return (
            <div key={project.id} className="project-card">
              <div className="project-header">
                <div className="project-header-main">
                  <div className="project-title-section">
                    <h2
                      className="project-name"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleProjectExpansion(project.id, e);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {project.name}
                    </h2>
                    <ProjectTypeBadge type={project.type} />
                    <ProjectStatusBadge status={project.status} />
                    <span
                      className={`pass-rate ${
                        project?.pass_rate < 70
                          ? "low"
                          : project?.pass_rate < 90
                          ? "medium"
                          : "high"
                      }`}
                    >
                      {project?.pass_rate}% Pass Rate
                    </span>
                  </div>

                  <div className="project-header-stats">
                    <div className="stat-header">
                      <span className="stat-header-label">Test Cases</span>
                      <span className="stat-header-value">
                        {project.total_test_cases}
                      </span>
                    </div>
                    <div className="stat-header">
                      <span className="stat-header-label">Passed</span>
                      <span className="stat-header-value passed">
                        {project.total_passed}
                      </span>
                    </div>

                    <div className="stat-header">
                      <span className="stat-header-label">Failed</span>
                      <span className="stat-header-value failed">
                        {project.total_failed}
                      </span>
                    </div>
                    <div className="stat-header">
                      <span className="stat-header-label">Pending</span>
                      <span className="stat-header-value pending">
                        {project.total_pending}
                      </span>
                    </div>
                  </div>

                  <button
                    className={`expand-toggle ${
                      expandedProjects[project.id] ? "expanded" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleProjectExpansion(project.id, e);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                </div>

                {/* <p className="project-description">{project.description}</p> */}
              </div>

              {expandedProjects[project.id] && (
                <div className="project-content expanded">
                  <div className="project-actions flex flex-nowrap items-center justify-between gap-3 w-full mb-4">
                    <div className="project-actions-controls flex flex-nowrap items-center gap-3 flex-1 min-w-0 overflow-x-auto">
                      <div className="search-container relative flex-none w-[220px] m-0">
                        <input
                          type="text"
                          placeholder="Search test cases..."
                          value={projectTestCaseSearchTerms[project.id] || ""}
                          onChange={(e) =>
                            handleTestCaseSearch(project.id, e.target.value)
                          }
                          className="search-input !mt-0 h-9 w-full box-border pl-8 pr-3 border border-gray-300 rounded text-sm"
                        />
                        <svg
                          className="search-icon absolute left-2 top-1/2 -translate-y-1/2 !mt-0 text-gray-500 pointer-events-none"
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      </div>
                      <div className="filter-group flex flex-row items-center gap-2 flex-shrink-0">
                        <label className="text-sm text-gray-500 leading-none whitespace-nowrap">
                          Status:
                        </label>
                        <select
                          value={projectTestCaseStatusFilters[project.id] || "all"}
                          onChange={(e) =>
                            handleTestCaseStatusFilterChange(
                              project.id,
                              e.target.value
                            )
                          }
                          className="filter-select h-9 min-w-[100px] box-border px-2 rounded border border-gray-300 text-sm"
                        >
                          <option value="all">All</option>
                          <option value="passed">Passed</option>
                          <option value="failed">Failed</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>

                      <div className="filter-group flex flex-row items-center gap-2 flex-shrink-0 relative">
                        <label className="text-sm text-gray-500 leading-none whitespace-nowrap">
                          Story:
                        </label>
                        <select
                          value={selectedStories[project.id] || ""}
                          onChange={(e) =>
                            handleStoryFilterChange(project.id, e.target.value)
                          }
                          className="filter-select h-9 min-w-[100px] box-border px-2 rounded border border-gray-300 text-sm"
                          disabled={storiesLoading[project.id]}
                        >
                          <option value="">All Stories</option>
                          {projectStories[project.id] &&
                            projectStories[project.id].map((story) => (
                              <option key={story.id} value={story.id}>
                                {story.story_key}
                              </option>
                            ))}
                        </select>
                        {storiesLoading[project.id] && (
                          <div className="filter-loading">
                            <div className="spinner-small"></div>
                          </div>
                        )}
                      </div>

                      <div className="filter-group flex flex-row items-center gap-2 flex-shrink-0">
                        <label className="text-sm text-gray-500 leading-none whitespace-nowrap">
                          Type:
                        </label>
                        <select
                          value={projectTestCaseTypeFilters[project.id] || "all"}
                          onChange={(e) =>
                            handleTestCaseTypeFilterChange(
                              project.id,
                              e.target.value
                            )
                          }
                          className="filter-select h-9 min-w-[100px] box-border px-2 rounded border border-gray-300 text-sm"
                        >
                          <option value="all">All Types</option>
                          {TEST_CASE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="filter-group flex flex-row items-center gap-2 flex-shrink-0">
                        <label className="text-sm text-gray-500 leading-none whitespace-nowrap">
                          Priority:
                        </label>
                        <select
                          value={
                            projectTestCasePriorityFilters[project.id] || ""
                          }
                          onChange={(e) =>
                            handleTestCasePriorityFilterChange(
                              project.id,
                              e.target.value
                            )
                          }
                          className="filter-select h-9 min-w-[100px] box-border px-2 rounded border border-gray-300 text-sm"
                        >
                          <option value="">All</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      <button
                        className="navbar-button !mt-0 inline-flex items-center justify-center gap-2 h-9 px-4 whitespace-nowrap flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={() => setCompileOptionsProjectId(project.id)}
                        disabled={compilingAllProjectId === project.id}
                        title="Compile and generate script for all AI generated and manual test cases"
                      >
                        {compilingAllProjectId === project.id ? (
                          <svg
                            className="animate-spin"
                            width="16"
                            height="16"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="16 18 22 12 16 6"></polyline>
                            <polyline points="8 6 2 12 8 18"></polyline>
                          </svg>
                        )}
                        {compilingAllProjectId === project.id
                          ? "Generating..."
                          : "Compile and Generate"}
                      </button>

                      <button
                        className="navbar-button create-test-case-btn !mt-0 inline-flex items-center justify-center gap-2 h-9 px-4 whitespace-nowrap flex-shrink-0"
                        onClick={() => {
                          setSelectedProjectId(project.id);
                          setSelectedTestSuiteId(null);
                          handleCreateTestCaseManuallyClick();
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Create Test Case
                      </button>
                    </div>

                    <button
                      className={`navbar-button refresh-test-cases-btn !mt-0 ml-auto flex-shrink-0 inline-flex items-center justify-center h-9 w-9 p-0${
                        loadingProjectId === project.id ? " refreshing" : ""
                      }`}
                      onClick={() => handleRefreshTestCases(project.id)}
                      disabled={loadingProjectId === project.id}
                      title="Refresh test cases"
                      aria-label="Refresh test cases"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                      </svg>
                    </button>
                  </div>

                  {testCasesLoading && loadingProjectId === project.id ? (
                    <div className="test-cases-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading test cases...</p>
                    </div>
                  ) : projectTestCases.length > 0 &&
                    expandedProjects[project.id] ? (
                    <div className="test-cases-table-container flex flex-col min-h-0 overflow-hidden">
                      <div className="flex-1 min-h-0 max-h-[calc(100vh-360px)] overflow-y-auto overflow-x-auto">
                      <table className="test-cases-table">
                        <thead>
                          <tr className="[&>th]:sticky [&>th]:top-0 [&>th]:z-[1] [&>th]:bg-[#f8f9fa]">
                            <th>Sr.No</th>
                            <th
                              onClick={() =>
                                handleSortChange(project.id, "name")
                              }
                              className="sortable test-case-name-col"
                            >
                              Name {renderSortIndicator(project.id, "name")}
                            </th>
                            <th
                              onClick={() =>
                                handleSortChange(project.id, "environment")
                              }
                              className="sortable environment-col"
                            >
                              Environment{" "}
                              {renderSortIndicator(project.id, "environment")}
                            </th>
                            <th>Priority</th>
                            <th style={{ textAlign: "center" }}>
                              Self-Heal
                            </th>
                            <th>Status</th>
                            <th style={{ textAlign: "center" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectTestCases.map((testCase, index) => {
                            const displayNumber =
                              ((projectTestCasePages[project.id] || 1) - 1) *
                                testCasesPerPage +
                              index +
                              1;
                            const isDiscoveryFlow = usesDiscoveryFlow(testCase);
                            const isScriptGenerated =
                              testCase.automation_status === "generated";
                            const isCompiling = compilingTestCaseId === testCase.id;
                            // Discovery flow test cases need a compiled script before view / run
                            const canUseScript = !isDiscoveryFlow || isScriptGenerated;
                            const scriptRequiredTitle = canUseScript
                              ? undefined
                              : "Compile and generate the script first";

                            return (
                            <tr
                              key={testCase.id}
                              className="test-case-row"
                              // onClick={() =>
                              //   handleViewTestCase(testCase, project.name)
                              // }
                            >
                              <td>
                                <div className="test-case-id-cell">
                                  {/* <input
                                    type="checkbox"
                                    checked={selectedTestCases.includes(
                                      testCase.id
                                    )}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleTestCaseSelection(
                                        testCase.id,
                                        e.target.checked
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ cursor: "pointer" }}
                                  /> */}
                                  <span>{displayNumber}.</span>
                                </div>
                              </td>
                              <td
                                className="test-case-name"
                                title={testCase.name}
                                onClick={() =>
                                  handleViewTestCase(testCase, project.name)
                                }
                              >
                                <span className="test-case-name-text">
                                  {testCase.name}
                                </span>
                              </td>
                              <td className="environment-cell" title={testCase.environment}>
                                <span className="environment-text">
                                  {testCase.environment}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`priority-badge priority-${testCase?.priority}`}
                                >
                                  {testCase?.priority?.charAt(0).toUpperCase() +
                                    testCase?.priority?.slice(1) || 'N/A'}
                                </span>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <SelfHealBadge
                                  isHealed={testCase?.is_healed}
                                  onClick={() => handleOpenHealingModal(testCase)}
                                  testCaseId={testCase?.test_case_id || testCase?.id}
                                />
                              </td>
                              <td>
                                <span
                                  className={`status-badge status-${testCase?.status?.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {testCase?.status?.charAt(0).toUpperCase() +
                                    testCase?.status?.slice(1)}
                                </span>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <div
                                  className="test-case-actions"
                                  style={{
                                    position: "relative",
                                    display: "inline-block",
                                  }}
                                >
                                  <button
                                    className="action-menu-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveActionDropdown((prev) =>
                                        prev === testCase.id ? null : testCase.id
                                      );
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: "4px 8px",
                                      fontSize: "18px",
                                      color: "#666",
                                      transition: "color 0.2s",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.color = "#000")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.color = "#666")
                                    }
                                    title="More options"
                                  >
                                    ⋮
                                  </button>
                                  <div
                                    className="action-dropdown"
                                    style={{
                                      display:
                                        activeActionDropdown === testCase.id
                                          ? "block"
                                          : "none",
                                      position: "absolute",
                                      top: "100%",
                                      right: 0,
                                      backgroundColor: "white",
                                      border: "1px solid #ddd",
                                      borderRadius: "4px",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                      zIndex: 1000,
                                      minWidth: "180px",
                                    }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.currentTarget.closest(
                                          ".action-dropdown"
                                        ).style.display = "none";
                                        handleEditTestCaseFromList(
                                          testCase,
                                          project
                                        );
                                      }}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "none",
                                        background: "none",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        color: "#333",
                                        transition: "background-color 0.2s",
                                        borderBottom: "1px solid #f0f0f0",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor =
                                          "#f5f5f5")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      <svg
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          marginRight: "8px",
                                          flexShrink: 0,
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                      </svg>
                                      Edit Test Case
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveActionDropdown(null);
                                        handleViewScript(testCase);
                                      }}
                                      disabled={!canUseScript}
                                      title={scriptRequiredTitle}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "none",
                                        background: "none",
                                        textAlign: "left",
                                        cursor: canUseScript ? "pointer" : "not-allowed",
                                        opacity: canUseScript ? 1 : 0.5,
                                        fontSize: "14px",
                                        color: "#333",
                                        transition: "background-color 0.2s",
                                        borderBottom: "1px solid #f0f0f0",
                                      }}
                                      onMouseEnter={(e) =>
                                        canUseScript &&
                                        (e.target.style.backgroundColor =
                                          "#f5f5f5")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      <svg
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          marginRight: "8px",
                                          flexShrink: 0,
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                      </svg>
                                      View Script
                                    </button>
                                    {isDiscoveryFlow && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveActionDropdown(null);
                                          handleCompileScript(testCase);
                                        }}
                                        disabled={isCompiling}
                                        title={
                                          isScriptGenerated
                                            ? "Recompile and generate script"
                                            : "Compile and generate script"
                                        }
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          width: "100%",
                                          padding: "10px 12px",
                                          border: "none",
                                          background: "none",
                                          textAlign: "left",
                                          cursor: isCompiling ? "not-allowed" : "pointer",
                                          opacity: isCompiling ? 0.6 : 1,
                                          fontSize: "14px",
                                          color: "#333",
                                          transition: "background-color 0.2s",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) =>
                                          !isCompiling &&
                                          (e.target.style.backgroundColor =
                                            "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.target.style.backgroundColor =
                                            "transparent")
                                        }
                                      >
                                        {isCompiling ? (
                                          <svg
                                            className="animate-spin"
                                            style={{
                                              width: "16px",
                                              height: "16px",
                                              marginRight: "8px",
                                              flexShrink: 0,
                                            }}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            />
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            style={{
                                              width: "16px",
                                              height: "16px",
                                              marginRight: "8px",
                                              flexShrink: 0,
                                            }}
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            viewBox="0 0 24 24"
                                          >
                                            <polyline points="16 18 22 12 16 6" />
                                            <polyline points="8 6 2 12 8 18" />
                                          </svg>
                                        )}
                                        {isCompiling
                                          ? "Generating Script..."
                                          : isScriptGenerated
                                          ? "Recompile & Generate"
                                          : "Compile & Generate"}
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveActionDropdown(null);
                                        handleRunTestCaseFromList(
                                          testCase,
                                          project.name
                                        );
                                      }}
                                      disabled={!canUseScript}
                                      title={scriptRequiredTitle}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "none",
                                        background: "none",
                                        textAlign: "left",
                                        cursor: canUseScript ? "pointer" : "not-allowed",
                                        opacity: canUseScript ? 1 : 0.5,
                                        fontSize: "14px",
                                        color: "#333",
                                        transition: "background-color 0.2s",
                                        borderBottom: "1px solid #f0f0f0",
                                      }}
                                      onMouseEnter={(e) =>
                                        canUseScript &&
                                        (e.target.style.backgroundColor =
                                          "#f5f5f5")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      <svg
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          marginRight: "8px",
                                          flexShrink: 0,
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <polygon
                                          points="5 3 19 12 5 21 5 3"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                        />
                                      </svg>
                                      Run Test Case
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveActionDropdown(null);
                                        handleDeleteTestCaseFromList(testCase);
                                      }}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "none",
                                        background: "none",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        color: "#dc3545",
                                        transition: "background-color 0.2s",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor =
                                          "#fff5f5")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      <svg
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          marginRight: "8px",
                                          flexShrink: 0,
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>

                      <Pagination
                        page={projectTestCasePages[project.id] || 1}
                        totalPages={projectTestCaseTotalPages[project.id] || 1}
                        onPageChange={(page) =>
                          handleTestCasePageChange(project.id, page)
                        }
                        showPageSize={false}
                        showRange={false}
                      />
                    </div>
                  ) : !testCasesLoading && expandedProjects[project.id] ? (
                    <div className="test-cases-empty-state">
                      <p>
                        {selectedStories[project.id]
                          ? `No test cases found for selected story.`
                          : projectTestCaseSearchTerms[project.id] ||
                            (projectTestCaseStatusFilters[project.id] &&
                              projectTestCaseStatusFilters[project.id] !==
                                "all") ||
                            projectTestCasePriorityFilters[project.id]
                          ? "No test cases found matching your filters."
                          : "No test cases found for this project."}
                      </p>

                      <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md border-0 cursor-pointer shadow-sm hover:shadow-md transition-all duration-200"
                        onClick={() => {
                          setSelectedProjectId(project.id);
                          setSelectedTestSuiteId(null);
                          handleCreateTestCaseManuallyClick();
                        }}
                    
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        <span className="leading-none">Create Test Case</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="project-pagination">
        <Pagination
          page={projectPageFromRoute}
          totalPages={totalProjectPages}
          onPageChange={handleProjectPageChange}
          showPageSize={false}
          showRange={false}
        />
      </div>

      {/* Compile and Generate Scripts Options Modal */}
      <CompileScriptsOptionsModal
        isOpen={Boolean(compileOptionsProjectId)}
        onClose={() => setCompileOptionsProjectId(null)}
        onGenerate={(testCaseType) =>
          handleCompileAllScripts(compileOptionsProjectId, testCaseType)
        }
      />

      {/* Create Test Case Options Modal */}
      <CreateTestCaseOptionsModal
        isOpen={isCreateTestCaseOptionsModalOpen}
        onClose={() => setIsCreateTestCaseOptionsModalOpen(false)}
        onCreateTestCase={handleCreateTestCaseOption}
        onCreateViaExtension={handleCreateTestCaseViaExtension}
        projectId={selectedProjectId}
        projectName={
          projects.find((p) => p.id === selectedProjectId)?.name || ""
        }
      />

      {/* Extension Recording Modal (create + edit) */}
      <ExtensionRecordingModal
        isOpen={isExtensionRecordingModalOpen}
        onClose={handleCloseExtensionRecordingModal}
        onRecordingStarted={handleRecordingStarted}
        onTestCaseUpdated={handleTestCaseEnvUpdated}
        mode={extensionRecordingModalMode}
        testCaseId={editingTestCaseId}
        projectId={selectedProjectId}
        projectName={
          projects.find((p) => p.id === selectedProjectId)?.name || ""
        }
      />

      {/* Create Test Case Modal */}
      <CreateTestCaseModal
        isOpen={isCreateTestCaseModalOpen}
        onClose={() => setIsCreateTestCaseModalOpen(false)}
        onCreateTestCase={handleCreateTestCase}
        testSuiteId={selectedTestSuiteId}
        projectId={
          selectedProjectId || (projectFilter !== "all" ? projectFilter : null)
        }
        projectName={
          projects.find(
            (p) =>
              p.id ===
              (selectedProjectId ||
                (projectFilter !== "all" ? parseInt(projectFilter) : null))
          )?.name || "Selected Project"
        }
      />

      {/* View Test Case Modal */}
      <ViewTestCaseModal
        isOpen={isViewTestCaseModalOpen}
        onClose={() => setIsViewTestCaseModalOpen(false)}
        testCase={selectedTestCase}
        onEdit={handleEditTestCase}
        onDelete={handleDeleteTestCase}
        onRunTest={handleRunTestCase}
        onExecutionComplete={handleTestExecutionComplete}
        onApplySuggestion={handleApplySuggestion}
        onApplyUpdates={handleApplyUpdates}
        onSubmitReview={handleSubmitReview}
      />

      {/* CI/CD Integration Modal */}
      <CICDIntegrationModal
        isOpen={isCICDModalOpen}
        onClose={() => setIsCICDModalOpen(false)}
        onPushToFramework={handlePushToFramework}
        projectId={
          selectedProjectId || (projects.length > 0 ? projects[0].id : null)
        }
        token={accessToken}
      />

      {/* Edit Test Case Modal */}
      <EditTestCaseModal
        isOpen={isEditTestCaseModalOpen}
        onClose={() => setIsEditTestCaseModalOpen(false)}
        onSaveTestCase={handleSaveTestCase}
        testCase={selectedTestCase}
      />

      {/* Export Options Modal */}
      <ImportTestCasesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImported={handleImportSuccess}
        projectId={contextProject?.id || ""}
        projectName={contextProject?.name || ""}
      />

      <ExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        itemType="testCase"
        selectedItems={selectedTestCases
          .map((id) => testCases.find((tc) => tc.id === id))
          .filter(Boolean)}
        onExport={handleExport}
      />

      {/* View Script Modal - Inline */}
      {isViewScriptModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 8,
              width: "90%",
              maxWidth: "1000px",
              height: "85%",
              padding: 24,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                  Test Case Script: {selectedTestCaseForScript?.name}
                </h2>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  {isScriptReadOnly
                    ? "View the test automation script"
                    : "Edit and save your test automation script"}
                </p>
              </div>
              <button
                onClick={closeScriptModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                flex: 1,
                border: "1px solid #ccc",
                borderRadius: 4,
                position: "relative",
                marginBottom: 16,
              }}
            >
              <div
                style={{ position: "absolute", width: "100%", height: "100%" }}
              >
                <Editor
                  height="100%"
                  defaultLanguage="python"
                  theme="vs-dark"
                  value={editorContent}
                  onChange={(value) => {
                    if (!isScriptReadOnly) {
                      setEditorContent(value || "");
                    }
                  }}
                  options={{
                    readOnly: isScriptReadOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    automaticLayout: true,
                  }}
                  beforeMount={(monaco) => {
                    monaco.editor.defineTheme("customTheme", {
                      base: "vs-dark",
                      inherit: true,
                      rules: [],
                      colors: {
                        "editor.background": "#1e1e1e",
                      },
                    });
                    monaco.editor.setTheme("customTheme");
                  }}
                />
              </div>
            </div>

            {!isScriptReadOnly && (
              <div
                style={{
                  marginTop: "auto",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  onClick={closeScriptModal}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveScript}
                  disabled={isSavingScript}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 4,
                    backgroundColor: isSavingScript ? "#9ca3af" : "#7c3aed",
                    color: "white",
                    cursor: isSavingScript ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  {isSavingScript && (
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  {isSavingScript ? "Saving..." : "Save Script"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Run Test Case Modal for manual / AI generated (UI Discovery flow) */}
      <RunDiscoveryTestCaseModal
        isOpen={
          isRunTestCaseModalOpen && usesDiscoveryFlow(selectedTestCaseForRun)
        }
        onClose={closeRunTestCaseModal}
        onSuccess={handleDiscoveryRunComplete}
        testCaseId={selectedTestCaseForRun?.id}
        testCaseName={selectedTestCaseForRun?.name}
      />

      {/* Edit Test Steps Modal for manual / AI generated (UI Discovery flow) */}
      <EditDiscoveryStepsModal
        isOpen={!!testCaseForStepsEdit}
        onClose={() => setTestCaseForStepsEdit(null)}
        onUpdated={(testCase) => handleTestCaseEnvUpdated(testCase.project_id)}
        testCase={testCaseForStepsEdit}
      />

      {/* Run Test Case Modal for recorded test cases (from list actions) */}
      <RunTestCaseModal
        isOpen={
          isRunTestCaseModalOpen && !usesDiscoveryFlow(selectedTestCaseForRun)
        }
        onClose={closeRunTestCaseModal}
        testCaseId={selectedTestCaseForRun?.id}
        testCaseName={selectedTestCaseForRun?.name}
        projectId={selectedTestCaseForRun?.project_id}
        projectName={selectedTestCaseForRun?.projectName}
        ai_generated={selectedTestCaseForRun?.ai_generated}
        environment={selectedTestCaseForRun?.environment}
        environmentId={selectedTestCaseForRun?.environment_id}
      />

      {/* Delete Confirmation Modal (from list actions) */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        itemName={testCaseToDelete?.name}
        itemType="test case"
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTestCaseToDelete(null);
        }}
        onConfirm={confirmDeleteTestCase}
        isProcessing={isDeleting}
      />

      {/* Healing Modal */}
      <HealingModal
        isOpen={isHealingModalOpen}
        onClose={() => {
          setIsHealingModalOpen(false);          
          setSelectedTestCaseForHealing(null);
        }}
        testCaseId={ selectedTestCaseForHealing?.id || selectedTestCaseForHealing?.test_case_id }
        testCaseName={selectedTestCaseForHealing?.name}
        onHealingComplete={handleHealingComplete}
      />
    </div>
  );
};

export default TestCasesOverview;
